// usePaymentStore.js
import { defineStore } from "pinia";
import * as paymentService from "@/services/paymentService";

export const usePaymentStore = defineStore("payment", {
  state: () => ({
    payments: [],
    selectedPaymentIds: [],
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    // Filtro de userId enviado ao backend (mantido separado para compatibilidade
    // com setUserIdFilter, chamado por fluxos externos como UserForm)
    userIdFilter: null,

    shouldForcePageOne: false,

    // Filtros client-side espelhados no painel SearchPaymentInput.
    // O getter filteredPayments aplica esses filtros localmente sobre a página
    // atual enquanto o debounce está em voo — evita "piscar" na tabela.
    filters: {
      paymentId: "",
      subscriptionId: "",
      userId: "",
      status: "",
      mpPaymentId: "",
      couponCodeSnapshot: "",
      paymentMethod: "",
    },
  }),

  getters: {
    // Filtragem client-side instantânea — espelho do que o backend retorna
    // após o debounce convergir. Mantém a tabela responsiva durante a digitação.
    filteredPayments: (state) => {
      const f = state.filters;

      return state.payments.filter((p) => {
        // ── ID do payment ─────────────────────────────────────────────────
        if (f.paymentId.trim() !== "") {
          if (!String(p.paymentId ?? "").includes(f.paymentId.trim()))
            return false;
        }
        // ── ID da assinatura ──────────────────────────────────────────────
        if (f.subscriptionId.trim() !== "") {
          if (!String(p.subscriptionId ?? "").includes(f.subscriptionId.trim()))
            return false;
        }
        // ── ID do usuário ─────────────────────────────────────────────────
        if (f.userId.trim() !== "") {
          if (!String(p.userId ?? "").includes(f.userId.trim())) return false;
        }
        // ── Status (igualdade exata, case-insensitive) ────────────────────
        if (f.status.trim() !== "") {
          const ps = (p.status ?? "").toLowerCase();
          if (ps !== f.status.trim().toLowerCase()) return false;
        }
        // ── MpPaymentId (substring textual) ───────────────────────────────
        if (f.mpPaymentId.trim() !== "") {
          const mp = (p.mpPaymentId ?? "").toLowerCase();
          if (!mp.includes(f.mpPaymentId.trim().toLowerCase())) return false;
        }
        // ── Código do cupom snapshot (substring textual) ──────────────────
        if (f.couponCodeSnapshot.trim() !== "") {
          const cc = (p.couponCodeSnapshot ?? "").toLowerCase();
          if (!cc.includes(f.couponCodeSnapshot.trim().toLowerCase()))
            return false;
        }
        // ── Método de pagamento (substring textual) ───────────────────────
        if (f.paymentMethod.trim() !== "") {
          const pm = (p.paymentMethod ?? "").toLowerCase();
          if (!pm.includes(f.paymentMethod.trim().toLowerCase())) return false;
        }
        return true;
      });
    },

    isAllSelected: (state) => (total) =>
      state.selectedPaymentIds.length === total,
  },

  actions: {
    // ─── Seleção ─────────────────────────────────────────────────────────────
    toggleSelection(id) {
      const index = this.selectedPaymentIds.indexOf(id);
      if (index > -1) {
        this.selectedPaymentIds.splice(index, 1);
      } else {
        this.selectedPaymentIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedPaymentIds = [...ids];
    },

    clearSelection() {
      this.selectedPaymentIds = [];
    },

    // ─── Fetch paginado ───────────────────────────────────────────────────────
    async fetchPaymentsPaged(pageNumber, pageSize, legacyUserIdFilter) {
      try {
        // Mescla legacyUserIdFilter (fluxo antigo via setUserIdFilter) com
        // os filtros do painel. O campo userId do painel tem prioridade.
        const filters = {
          paymentId: this.filters.paymentId,
          subscriptionId: this.filters.subscriptionId,
          userId:
            this.filters.userId ||
            (legacyUserIdFilter != null ? String(legacyUserIdFilter) : ""),
          status: this.filters.status,
          mpPaymentId: this.filters.mpPaymentId,
          couponCodeSnapshot: this.filters.couponCodeSnapshot,
          paymentMethod: this.filters.paymentMethod,
        };

        const response = await paymentService.fetchPaymentsPaged(
          pageNumber,
          pageSize,
          filters,
        );

        this.payments = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = { message };
        console.error("❌ Erro ao buscar payments paginados:", message);
        throw new Error(message);
      }
    },

    // ─── Filtros ──────────────────────────────────────────────────────────────
    setUserIdFilter(userId) {
      this.userIdFilter = userId;
      this.filters.userId = userId != null ? String(userId) : "";
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchPaymentsPaged(1, this.pageSize, userId);
    },

    setFilter(field, value) {
      if (!(field in this.filters)) return;
      this.filters[field] = value;

      if (field === "userId") {
        // userId mantém o fluxo legado: valida inteiro positivo e chama setUserIdFilter
        const trimmed = String(value ?? "").trim();
        const parsed = trimmed === "" ? null : Number(trimmed);
        const isValid =
          trimmed === "" || (Number.isInteger(parsed) && parsed > 0);
        if (isValid) this.setUserIdFilter(parsed);
        return;
      }

      // Demais campos: dispara refetch voltando à página 1
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchPaymentsPaged(1, this.pageSize, this.userIdFilter);
    },

    clearFilters(refetch = true) {
      this.filters = {
        paymentId: "",
        subscriptionId: "",
        userId: "",
        status: "",
        mpPaymentId: "",
        couponCodeSnapshot: "",
        paymentMethod: "",
      };
      this.userIdFilter = null;
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      if (refetch) {
        this.fetchPaymentsPaged(1, this.pageSize, null);
      }
    },

    // ─── CRUD ─────────────────────────────────────────────────────────────────
    async createPayment(data) {
      try {
        await paymentService.createPayment(data);
        this.shouldForcePageOne = true;
        await this.fetchPaymentsPaged(1, this.pageSize, this.userIdFilter);
      } catch (error) {
        console.error(
          "❌ Erro ao criar payment:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async updatePayment(data) {
      try {
        await paymentService.updatePayment(data);
        await this.fetchPaymentsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar payment:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async deletePaymentById(id) {
      try {
        await paymentService.deletePayment(id);
        await this.fetchPaymentsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        // Se a página atual ficar vazia, volta para a anterior
        if (this.payments.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchPaymentsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir payment:", message);
        throw new Error(message);
      }
    },

    async deleteSelectedPayments() {
      if (!this.selectedPaymentIds.length) return;
      try {
        await paymentService.deletePaymentsBatch(this.selectedPaymentIds);
        this.clearSelection();
        await this.fetchPaymentsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.payments.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchPaymentsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir payments em lote:", message);
        throw new Error(message);
      }
    },
  },
});
